import React, { useState, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { cssMap as cssMapUnbounded } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	useRouteName,
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG,
	VIEW_BLOG_DATE_LEGACY,
} from '@confluence/named-routes/entry-points/routes.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { BANNER_HEIGHT } from './BannerSize.ts';

const useContentRecommendationParam = createSingleQueryParamHook('contentRecommendation');
const usePopularParam = createSingleQueryParamHook('popular');

const unboundedStyles = cssMapUnbounded({
	root: {
		// classic pages are 100% + 80px but we have to fill the container's left margin
		width: 'calc(100% + var(--full-cover-image-margin, 0)/2 )',
		marginLeft: 'var(--full-cover-image-margin, 0px)',
		paddingInline: token('space.600'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('color.background.accent.blue.subtlest'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height: `${BANNER_HEIGHT}px`,
		position: 'relative',
		overflow: 'hidden',
	},
	headingStyles: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 1,
		WebkitBoxOrient: 'vertical',
	},
	subtextStyles: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
	},
	livePageStyle: {
		marginLeft: token('space.0'),
		// ensures padding is subtracted from the inside for live pages
		width: 'auto',
		borderTop: 'none',
	},
});

const styles = cssMap({
	contentContainerStyles: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		gap: token('space.100'),
		zIndex: 1,
		padding: token('space.300'),
		width: '50%',
		overflow: 'hidden',
	},
	svgContainer: {
		width: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
	closeButton: {
		position: 'absolute',
		top: token('space.150'),
		right: token('space.150'),
		pointerEvents: 'auto',
	},
});

const i18n = defineMessages({
	bannerWelcomeMessage: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.heading',
		defaultMessage: 'Welcome to Confluence',
		description:
			'The text is shown as the banner title at the top of a recommended Confluence page when a new user lands on content selected to help them get started. It welcomes the user and frames the page as an onboarding recommendation.',
	},
	bannerWelcomeMessageWithName: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.heading.with-name',
		defaultMessage: 'Welcome to Confluence, {firstName}',
		description:
			"The text is shown as the banner title at the top of a recommended Confluence page, personalized with the user's first name. It welcomes the user and frames the page as an onboarding recommendation.",
	},
	bannerDefaultSubtext: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.no.teammates.subtext',
		defaultMessage:
			'This page was handpicked for you based on your role to help you dive into what’s most relevant as you start in Confluence.',
		description:
			'The text is shown as supporting body text under the banner title at the top of a recommended Confluence page when the recommendation is based on the user’s role (and not on teammates). It explains why the user is seeing this page and encourages them to explore it as part of getting started.',
	},
	bannerTeammatesSubtext: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.with.teammates.subtext',
		defaultMessage:
			'This page was handpicked for you based on your role and teammates to help you dive into what’s most relevant as you start in Confluence.',
		description:
			'The text is shown as supporting body text under the banner title at the top of a recommended Confluence page when the recommendation is based on both the user’s role and their teammates. It explains why the user is seeing this page and encourages them to explore it as part of getting started.',
	},
	bannerDismissLabel: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.dismiss',
		defaultMessage: 'Dismiss banner',
		description:
			'The text is used as the accessible label for the close (X) button in the top-right of the recommendation banner. Activating the button hides/dismisses the banner for the current view.',
	},
	bannerPopularSubtext: {
		id: 'experiment-teammate-content-screen-page.teammate-content-recommendation-banner.with.popular.subtext',
		defaultMessage:
			'We’ve handpicked this page from your organization’s most popular content so you can dive into what people find most useful.',
		description:
			'The text is shown as supporting body text under the banner title at the top of a recommended Confluence page',
	},
});

type TeammateContentRecommendationBannerProps = {
	contentId: string;
	isLivePage: boolean;
};

const TeammateContentRecommendationBannerContent = ({ isLivePage }: { isLivePage: boolean }) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { displayName } = useSessionData();
	const { setQueryParams } = useRouteActions();
	const popularParam = usePopularParam();
	const isPopularMessageVariant = popularParam === 'true';

	const firstName = displayName?.split(' ')[0] ?? null;

	useEffect(() => {
		if (!isDismissed) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'teammateContentRecommendationBanner',
				},
			}).fire();
		}
	}, [isDismissed, createAnalyticsEvent]);

	const handleDismiss = () => {
		setIsDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'banner',
				actionSubjectId: 'teammateContentRecommendationBanner',
				source: 'viewPageContentContainer',
			},
		}).fire();
		setQueryParams({ contentRecommendation: undefined });
	};

	if (isDismissed) {
		return null;
	}

	return (
		<div
			data-vc="teammate-content-recommendation-banner"
			css={[unboundedStyles.root, isLivePage && unboundedStyles.livePageStyle]}
		>
			<Flex alignItems="center">
				<Box xcss={cx(styles.contentContainerStyles)}>
					<Box xcss={unboundedStyles.headingStyles}>
						<Heading size="large">
							{firstName ? (
								<FormattedMessage {...i18n.bannerWelcomeMessageWithName} values={{ firstName }} />
							) : (
								<FormattedMessage {...i18n.bannerWelcomeMessage} />
							)}
						</Heading>
					</Box>
					<Box xcss={unboundedStyles.subtextStyles}>
						<Text as="p">
							{isPopularMessageVariant ? (
								<FormattedMessage {...i18n.bannerPopularSubtext} />
							) : (
								<FormattedMessage {...i18n.bannerTeammatesSubtext} />
							)}
						</Text>
					</Box>
				</Box>
				<Box xcss={cx(styles.svgContainer)}>
					<Image
						src="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EQLY.svg"
						srcDark="https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EQLZ.svg"
						alt=""
					/>
				</Box>
			</Flex>
			<Box xcss={cx(styles.closeButton)}>
				<IconButton
					testId="teammate-content-button-close"
					appearance="subtle"
					icon={CrossIcon}
					label={i18n.bannerDismissLabel.defaultMessage}
					onClick={handleDismiss}
				/>
			</Box>
		</div>
	);
};

const TeammateContentRecommendationBannerInner = ({
	contentId,
	isLivePage,
}: TeammateContentRecommendationBannerProps) => {
	const contentRecommendationParam = useContentRecommendationParam();

	const isValidBannerRoute = useRouteName({
		selector: (routeName: string | undefined) =>
			routeName === VIEW_PAGE.name ||
			routeName === VIEW_BLOG.name ||
			routeName === VIEW_BLOG_DATE_LEGACY.name,
	});

	const shouldRenderBanner =
		isValidBannerRoute &&
		!!contentId &&
		contentRecommendationParam === 'on' &&
		fg('cc-engagement-nth-user-teammate-rollout-gate');

	if (!shouldRenderBanner) {
		return null;
	}

	return <TeammateContentRecommendationBannerContent isLivePage={isLivePage} />;
};

export const TeammateContentRecommendationBanner = ({
	contentId,
	isLivePage,
}: TeammateContentRecommendationBannerProps) => {
	return (
		<ErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
			<TeammateContentRecommendationBannerInner contentId={contentId} isLivePage={isLivePage} />
		</ErrorBoundary>
	);
};
